import { css } from '@emotion/react';

import { SECONDARY_FONT_FAMILY } from 'src/styles/constants';
import { getTheme } from 'src/utils/getTheme';

const { theme, isHosTheme } = getTheme();
export const styles = {
    container: css({
        display: 'flex',
        width: '100%',
        height: '100%',
        padding: '10px 20px 0 20px',
        flexDirection: 'column',
        textAlign: 'center',
        alignItems: 'center',
        position: 'relative',
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '230px',
            background: isHosTheme ? theme.palette.background.paper : theme.palette.common.black,
            zIndex: -1,
            // On small screens the stacked header's height varies, so the header draws its own band instead
            [theme.breakpoints.down('md')]: { display: 'none' },
        },
        [theme.breakpoints.up('md')]: { padding: '30px 20px 0 20px' },
    }),

    wrapper: css({
        display: 'flex',
        width: '650px',
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        flexDirection: 'column',
        textAlign: 'center',
        alignItems: 'center',
        rowGap: '40px',
    }),

    header: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: '40px',

        // A band that fits the header, ending a little below the inputs so the page background shows
        // between it and the results. The shadow bleeds it to the screen edges without causing a
        // horizontal scroll, and the clip extends it up over the container's top padding
        [theme.breakpoints.down('md')]: {
            paddingBottom: '30px',
            background: isHosTheme ? theme.palette.background.paper : theme.palette.common.black,
            boxShadow: `0 0 0 100vmax ${
                isHosTheme ? theme.palette.background.paper : theme.palette.common.black
            }`,
            clipPath: 'inset(-10px -100vmax 0)',
        },
    }),

    title: css({
        color: theme.palette.common.white,
        marginBottom: '-20px',
        '::before': {
            content: '""',
            display: 'block',
            backgroundColor: theme.palette.primary.main,
            position: 'absolute',
            height: '.4375rem',
            width: '4rem',
            marginTop: '-10px',
        },
        [theme.breakpoints.down('md')]: { marginTop: '20px' },
    }),

    subtitle: css({ color: theme.palette.common.white, fontSize: '18px', marginTop: '26px' }),

    searchPanel: css({
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
        gap: '16px',
        position: 'relative',
        zIndex: 1,

        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    }),

    resultsSection: css({
        width: 'calc(100vw - 40px)',
        paddingInline: '60px',
        marginTop: '20px',

        [theme.breakpoints.down('md')]: {
            width: '90vw',
            paddingInline: '0',
            marginLeft: '0',
            marginTop: '50px',
        },
    }),

    promo: css({
        position: 'relative',
        height: '500px',
        overflow: 'hidden',
        // Space before the footer
        marginBottom: '60px',

        [theme.breakpoints.down('lg')]: {
            height: '400px',
        },

        [theme.breakpoints.down('md')]: {
            marginBottom: '40px',
        },
    }),

    promoImage: css({
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    }),

    // Darkens the bottom of the image so the white text stays readable over any part of the photo
    promoContent: css({
        position: 'relative',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '24px',
        padding: '40px',
        textAlign: 'left',
        color: theme.palette.common.white,
        background:
            'linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.2) 55%, rgba(0, 0, 0, 0) 100%)',

        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            padding: '24px',
        },
    }),

    promoText: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    }),

    promoTitle: css({
        color: 'inherit',
        margin: 0,
    }),

    promoTagline: css({
        color: 'inherit',
        fontStyle: 'italic',
        fontSize: '18px',
    }),

    promoSlogan: css({
        display: 'flex',
        flexDirection: 'column',
        color: 'inherit',
        fontFamily: SECONDARY_FONT_FAMILY,
        fontWeight: 700,
        fontSize: '56px',
        lineHeight: 0.9,
        textTransform: 'uppercase',
        textAlign: 'right',

        [theme.breakpoints.down('md')]: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            columnGap: '0.25em',
            fontSize: '32px',
            textAlign: 'left',
        },
    }),

    promoSloganWord: css({
        display: 'block',
    }),

    resultsHeader: css({
        marginBottom: '24px',
        [theme.breakpoints.down('md')]: { marginBottom: '20px' },
    }),

    resultsTitle: css({
        color: theme.palette.common.black,
        textAlign: 'left',
        margin: 0,
        '::before': {
            content: '""',
            display: 'block',
            backgroundColor: theme.palette.primary.main,
            position: 'absolute',
            height: '.4375rem',
            width: '3rem',
            marginTop: '-10px',
        },
    }),

    resultsContainer: css({ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }),

    skeletonTitle: css({ width: '200px', height: '40px' }),

    skeletonCard: css({
        width: '100%',
        maxWidth: '1240px',
        height: '500px',
        margin: '0 auto 60px',

        [theme.breakpoints.down('lg')]: { height: '400px' },
    }),

    disclaimer: css({
        width: '100%',
        maxWidth: '1240px',
        boxSizing: 'border-box',
        margin: '-40px auto 2rem',
        padding: '30px 20px',
        textAlign: 'left',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,

        [theme.breakpoints.down('md')]: {
            marginTop: '-20px',
            padding: 0,
        },
    }),
};
